import { useMemo, useState } from "react";
import { theme } from "../lib/theme";
import { dictionaries } from "../lib/i18n";
import type { UiLang, Stop } from "../lib/types";

interface Props {
  stops: Stop[];
  activeIds: string[];
  lang: UiLang;
  onSelect: (stop: Stop) => void;
  onHover?: (stop: Stop | null) => void;
}

const WIDTH = 1000;
const HEIGHT = 300;
const PADDING = { top: 30, right: 30, bottom: 54, left: 52 };

/** Hvor mye hvert bakgrunnslag forskyves — gir dybde uten 3D-bibliotek. */
const LAYERS = [
  { offsetX: -26, offsetY: 20, scale: 1.35, opacity: 0.1 },
  { offsetX: -13, offsetY: 10, scale: 1.15, opacity: 0.16 },
];

/**
 * Terrenget langs ruten, tegnet som en fjellsilhuett i perspektiv.
 *
 * Bergensbanen klatrer fra havnivå i Oslo til 1222 moh på Finse og ned igjen
 * til fjorden. En strek-graf viser tallene, men ikke hva de betyr — så her
 * bygges profilen som fjell, med to forskjøvne bakgrunnslag som gir dybde og
 * en himmel som mørkner mot toppen. Alt er SVG: ingen tredjepartsbibliotek,
 * ingen WebGL, og det skalerer og skriver ut like godt som resten av siden.
 */
export function TerrainProfile({ stops, activeIds, lang, onSelect, onHover }: Props) {
  const t = dictionaries[lang];
  const [hovered, setHovered] = useState<string | null>(null);

  const { ridge, points, ticks, maxElevation } = useMemo(() => {
    const innerW = WIDTH - PADDING.left - PADDING.right;
    const innerH = HEIGHT - PADDING.top - PADDING.bottom;
    const peak = Math.max(400, ...stops.map((s) => s.elevationM));

    const mapped = stops.map((stop, index) => ({
      stop,
      x: PADDING.left + (stops.length === 1 ? innerW / 2 : (index / (stops.length - 1)) * innerW),
      y: PADDING.top + innerH * (1 - stop.elevationM / peak),
    }));

    // Myk kurve mellom toppene, så silhuetten leser som fjell og ikke som et diagram.
    const path = mapped
      .map((p, i) => {
        if (i === 0) return `M ${p.x.toFixed(1)} ${p.y.toFixed(1)}`;
        const prev = mapped[i - 1];
        const cx1 = prev.x + (p.x - prev.x) * 0.45;
        const cx2 = prev.x + (p.x - prev.x) * 0.55;
        return `C ${cx1.toFixed(1)} ${prev.y.toFixed(1)}, ${cx2.toFixed(1)} ${p.y.toFixed(1)}, ${p.x.toFixed(1)} ${p.y.toFixed(1)}`;
      })
      .join(" ");

    const base = PADDING.top + innerH;
    const tickValues = [0, Math.round(peak / 2 / 100) * 100, peak];

    return {
      ridge: { path, base, mapped },
      points: mapped,
      ticks: tickValues.map((value) => ({
        value,
        y: PADDING.top + innerH * (1 - value / peak),
      })),
      maxElevation: peak,
    };
  }, [stops]);

  if (stops.length === 0) return null;

  const active = new Set(activeIds);
  const closed = (path: string, base: number) =>
    `${path} L ${(WIDTH - PADDING.right).toFixed(1)} ${base} L ${PADDING.left.toFixed(1)} ${base} Z`;

  return (
    <svg
      className="map-svg terrain"
      // Se StopHoverCard.
      data-hover-source=""
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      role="img"
      aria-label={`${t.terrainTitle} — ${maxElevation} ${t.unitMasl}`}
    >
      <defs>
        <linearGradient id="sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#CFE0E7" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#F7F5F0" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="ridgeFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#43606B" />
          <stop offset="55%" stopColor={theme.color.fjord} />
          <stop offset="100%" stopColor="#16262C" />
        </linearGradient>
        <linearGradient id="snowline" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.75" />
          <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
        </linearGradient>
        {/* Klipper snødekket til selve fjellsilhuetten. */}
        <clipPath id="ridgeClip">
          <path d={closed(ridge.path, ridge.base)} />
        </clipPath>
      </defs>

      <rect
        x={PADDING.left}
        y={PADDING.top - 12}
        width={WIDTH - PADDING.left - PADDING.right}
        height={ridge.base - PADDING.top + 12}
        fill="url(#sky)"
      />

      {ticks.map((tick) => (
        <g key={tick.value}>
          <line
            x1={PADDING.left}
            x2={WIDTH - PADDING.right}
            y1={tick.y}
            y2={tick.y}
            stroke={theme.color.sand}
            strokeWidth={1}
            strokeDasharray="2 6"
          />
          {/*
            Høydetallet følger temaet, streken over gjør det ikke.

            `theme.color.mist` er én fast grå fra JS, og profilen står i et
            kort som snür med temaet. Målt: 2,92:1 i lys modus og 2,18:1 i
            mørk — altså for svak på *begge* flater, som er det som skjer når
            én farge skal dekke to. Rutenettstreken kan fortsatt være fast:
            den er en strek, ikke et tall man leser av.
          */}
          <text
            x={PADDING.left - 9}
            y={tick.y + 4}
            textAnchor="end"
            fontFamily={theme.font.mono}
            fontSize={10}
            style={{ fill: "var(--fjord-muted)" }}
          >
            {tick.value}
          </text>
        </g>
      ))}

      {/* Bakgrunnsåser: samme silhuett, forskjøvet og skalert. */}
      {LAYERS.map((layer, index) => (
        <g
          key={index}
          transform={`translate(${layer.offsetX} ${layer.offsetY}) scale(1 ${layer.scale})`}
          transform-origin={`0 ${ridge.base}`}
          opacity={layer.opacity}
        >
          <path d={closed(ridge.path, ridge.base)} fill={theme.color.fjord} />
        </g>
      ))}

      <path d={closed(ridge.path, ridge.base)} fill="url(#ridgeFill)" />

      {/* Snø over 900 moh, klippet til fjellet. */}
      <g clipPath="url(#ridgeClip)">
        <rect
          x={0}
          y={PADDING.top}
          width={WIDTH}
          height={Math.max(0, ((maxElevation - 900) / maxElevation) * (ridge.base - PADDING.top))}
          fill="url(#snowline)"
        />
      </g>

      <path d={ridge.path} fill="none" stroke="#7E98A3" strokeWidth={1.5} strokeOpacity={0.7} />

      {points.map(({ stop, x, y }) => {
        const isActive = active.has(stop.id) || hovered === stop.id;
        const showElevation = stop.elevationM >= 400 || isActive;

        return (
          <g
            key={stop.id}
            className="map-stop"
            onClick={() => onSelect(stop)}
            onMouseEnter={() => {
              setHovered(stop.id);
              onHover?.(stop);
            }}
            onMouseLeave={() => {
              setHovered(null);
              onHover?.(null);
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onSelect(stop);
              }
            }}
          >
            <rect x={x - 16} y={PADDING.top - 12} width={32} height={HEIGHT - PADDING.top} fill="transparent" />

            {/* Loddrett strek ned til bakken, som en kartnål. */}
            <line
              x1={x}
              x2={x}
              y1={y}
              y2={ridge.base}
              stroke={isActive ? theme.color.accent : "#FFFFFF"}
              strokeOpacity={isActive ? 0.8 : 0.16}
              strokeWidth={isActive ? 1.5 : 1}
            />

            <circle
              cx={x}
              cy={y}
              r={isActive ? 6 : 4}
              fill={isActive ? theme.color.accent : theme.color.birch}
              stroke={theme.color.fjord}
              strokeWidth={1.5}
            />

            {showElevation && (
              <text
                x={x}
                y={y - 12}
                textAnchor="middle"
                fontFamily={theme.font.mono}
                fontSize={10}
                fontWeight={700}
                /*
                 * Samme sak som høydeaksen over: `theme.color` er faste
                 * verdier fra JS, skrevet for det lyse kortet. Målt i mørk
                 * modus sto høyden på 1,07:1 og den valgte på 2,18:1 — altså
                 * tallet man leser av profilen, usynlig i halve appen.
                 */
                style={{ fill: isActive ? "var(--accent-ink)" : "var(--ink)" }}
              >
                {stop.elevationM}
              </text>
            )}

            <text
              x={x}
              y={HEIGHT - 30}
              textAnchor="middle"
              fontFamily={theme.font.mono}
              fontSize={10}
              fontWeight={isActive ? 700 : 400}
              style={{ fill: isActive ? "var(--accent-ink)" : "var(--fjord-muted)" }}
            >
              {stop.name.length > 13 ? `${stop.name.slice(0, 12)}…` : stop.name}
            </text>

            {stop.weather && (
              <text x={x} y={HEIGHT - 13} textAnchor="middle" fontSize={13}>
                {weatherGlyph(stop.weather.symbol)}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

/** Liten egen mapping — SVG-tekst tar emoji uten videre. */
function weatherGlyph(symbol: string): string {
  if (/^clearsky/.test(symbol)) return "☀️";
  if (/^fair/.test(symbol)) return "🌤️";
  if (/^partlycloudy/.test(symbol)) return "⛅";
  if (/^fog/.test(symbol)) return "🌫️";
  if (/snow|sleet/.test(symbol)) return "❄️";
  if (/rain/.test(symbol)) return "🌧️";
  if (/^cloudy/.test(symbol)) return "☁️";
  return "";
}
